import React, { useEffect, useMemo, useState } from "react";
import "./MyBookingPage.css";
import MybookingCard from "./custom comp/MybookingCard";
import MybookingCardSkeleton from "./custom comp/MybookingCardSkeleton";
import axios from "axios";
import { API_BASE_URL } from "../utils";
import MyInvitesCard from "./MyInvitesCard";

const API_TABLE = `${API_BASE_URL}/master/APIs/ClubTable/bookingHistory`;
const API_PACKAGE = `${API_BASE_URL}/master/APIs/ClubPackage/bookingHistory`;

export default function MyBookingPage() {
  const TABS = ["Party Packages", "Table Booking", "Events", "My Invites"];
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData")) || null;
    } catch {
      return null;
    }
  }, []);
  const userId = storedUser?.id ?? null;

  const [activeTab, setActiveTab] = useState(TABS[0]);

  const [packageBookings, setPackageBookings] = useState([]); // completed
  const [tableBookings, setTableBookings] = useState([]); // completed
  const [upcomingPackageBookings, setUpcomingPackageBookings] = useState([]); // pending
  const [upcomingTableBookings, setUpcomingTableBookings] = useState([]); // pending

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    const postForm = (url, type) => {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("type", type);
      return axios
        .post(url, fd, { signal })
        .then((r) => r?.data?.data ?? [])
        .catch((e) => {
          if (axios.isCancel(e)) return [];
          throw e;
        });
    };

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const [
          tablePendingRes,
          packagePendingRes,
          tableCompletedRes,
          packageCompletedRes,
        ] = await Promise.allSettled([
          postForm(API_TABLE, "pending"),
          postForm(API_PACKAGE, "pending"),
          postForm(API_TABLE, "completed"),
          postForm(API_PACKAGE, "completed"),
        ]);

        const safe = (p) =>
          p?.status === "fulfilled" && Array.isArray(p.value) ? p.value : [];

        setUpcomingTableBookings(safe(tablePendingRes));
        setUpcomingPackageBookings(safe(packagePendingRes));
        setTableBookings(safe(tableCompletedRes));
        setPackageBookings(safe(packageCompletedRes));
      } catch (e) {
        console.error("Booking history fetch failed:", e);
        setError("Could not load bookings. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }

    load();
    return () => controller.abort();
  }, [userId]);

  const renderSkeletons = (count = 3) =>
    Array.from({ length: count }).map((_, i) => (
      <MybookingCardSkeleton key={`s-${i}`} />
    ));

  const renderEmpty = (text = "No bookings found.") => (
    <div className="mbpswc-content-temp">
      <h2>{text}</h2>
    </div>
  );

  return (
    <div className="my-booking-Page">
      <div className="my-booking-Page-section">
        <div className="my-booking-Page-section-container">
          <div className="my-booking-Page-section-wrapper">
            <h1>My Booking</h1>

            <div className="my-booking-Page-section-wrapper-content">
              <div className="mbpswc-tabs">
                <div className="mbpswc-tabs-content">
                  {TABS.map((tab) => (
                    <div
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={
                        activeTab === tab
                          ? "mbpswc-tabs-content-tab-active"
                          : "mbpswc-tabs-content-tab"
                      }
                    >
                      {tab}
                    </div>
                  ))}
                </div>
              </div>

              {!userId && (
                <div className="mbpswc-content-temp">
                  <h2>Please log in to view your bookings.</h2>
                </div>
              )}

              {userId && error && (
                <div className="mbpswc-content-temp">
                  <h2>{error}</h2>
                </div>
              )}

              {userId && activeTab === "Party Packages" && (
                <div className="mbpswc-content">
                  <h1 className="mbpswc-content-title">
                    Upcoming &#40;{upcomingPackageBookings.length}&#41;
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {isLoading
                      ? renderSkeletons()
                      : upcomingPackageBookings.length === 0
                        ? renderEmpty("No upcoming package bookings.")
                        : upcomingPackageBookings.map((item, index) => (
                          <MybookingCard key={item?.id ?? index} data={item} />
                        ))}
                  </div>

                  <h1 className="mbpswc-content-title">
                    History &#40;{packageBookings.length}&#41;
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {isLoading
                      ? renderSkeletons()
                      : packageBookings.length === 0
                        ? renderEmpty("No past package bookings.")
                        : packageBookings.map((item, index) => (
                          <MybookingCard
                            key={item?.id ?? index}
                            data={item}
                            isHistory={true}
                          />
                        ))}
                  </div>
                </div>
              )}

              {userId && activeTab === "Table Booking" && (
                <div className="mbpswc-content">
                  <h1 className="mbpswc-content-title">
                    Upcoming &#40;{upcomingTableBookings.length}&#41;
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {isLoading
                      ? renderSkeletons()
                      : upcomingTableBookings.length === 0
                        ? renderEmpty("No upcoming table bookings.")
                        : upcomingTableBookings.map((item, index) => (
                          <MybookingCard key={item?.id ?? index} data={item} />
                        ))}
                  </div>

                  <h1 className="mbpswc-content-title">
                    History &#40;{tableBookings.length}&#41;
                  </h1>
                  <div className="mbpswc-content-cards-upcoming-history">
                    {isLoading
                      ? renderSkeletons()
                      : tableBookings.length === 0
                        ? renderEmpty("No past table bookings.")
                        : tableBookings.map((item, index) => (
                          <MybookingCard
                            key={item?.id ?? index}
                            data={item}
                            isHistory={true}
                          />
                        ))}
                  </div>
                </div>
              )}

              {activeTab === "Events" && (
                <div className="mbpswc-content-temp">
                  <h1>My Events coming soon</h1>
                </div>
              )}

              {activeTab === "My Invites" && (
                <div className="mbpswc-content-my-invites">
                  <MyInvitesCard />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
